<script setup lang="ts">
    import { computed, onMounted, shallowReactive, ref, watch, type Ref } from 'vue'
    import { useRoute } from 'vue-router'

    import PortfolioNavigation from '@common/Components/PortfolioNavigation.vue'
    import { MonthPickerDialogViewModel } from '@common/Components/MonthPickerDialog'
    import MonthPickerDialog from '@common/Components/MonthPickerDialog.vue'
    import { MonthYear, MonthYearHelpers, ProjectAcceptance } from '@common/types'

    import { AddProjectsDialogViewModel } from '@Quest/PortfolioScenarios/Components/AddProjectsDialog'
    import AddProjectsDialog from '@Quest/PortfolioScenarios/Components/AddProjectsDialog.vue'

    import PortfolioScenarioNavigation from '@Quest/PortfolioScenarios/Components/PortfolioScenarioNavigation.vue'

    import { ProjectPlanPickerDialogViewModel } from '@Quest/PortfolioScenarios/Components/ProjectPlanPickerDialog'
    import ProjectPlanPickerDialog from '@Quest/PortfolioScenarios/Components/ProjectPlanPickerDialog.vue'

    import type { ProjectsPageReadModel, ScenarioProjectPlanPickerReadModel, ScenarioProjectReadModel } from '@Quest/PortfolioScenarios/readmodels'

    import { useProjectsPageQueryV1, useScenarioProjectPlanPickerQueryV1 } from '@Quest/PortfolioScenarios/queries-vue-composables'

    import
        {
            useAddProjectsToScenarioCommandV1,
            useRemoveProjectsFromScenarioCommandV1,
            useUpdateProjectScenarioAcceptanceCommandV1,

            useLockProjectScenarioAcceptanceCommandV1,
            useUnlockProjectScenarioAcceptanceCommandV1,

            useUpdateProjectScenarioCommitmentDateCommandV1,
            useLockProjectScenarioCommitmentDateCommandV1,
            useUnlockProjectScenarioCommitmentDateCommandV1,

            useUpdateProjectScenarioStartDateCommandV1,
            useLockProjectScenarioStartDateCommandV1,
            useUnlockProjectScenarioStartDateCommandV1,

            useUpdateProjectScenarioMinStartDateCommandV1,
            useUpdateProjectScenarioMaxStartDateCommandV1,

            useUpdateProjectScenarioPlanLockCommandV1
        } from '@Quest/PortfolioScenarios/commands-vue-composables'

    import { LockProjectScenarioAcceptanceCommandV1, RemoveProjectsFromScenarioCommandV1, UnlockProjectScenarioAcceptanceCommandV1, UpdateProjectScenarioAcceptanceCommandV1, UpdateProjectScenarioCommitmentDateCommandV1, UpdateProjectScenarioMaxStartDateCommandV1, UpdateProjectScenarioMinStartDateCommandV1, UpdateProjectScenarioPlanLockCommandV1, UpdateProjectScenarioStartDateCommandV1 } from '../commands-core'


    const route = useRoute()

    const workspaceId = computed(() => Number(route.params?.workspaceId ?? 0))
    const tchProjectId = computed(() => Number(route.params?.tchProjectId ?? 0))
    const scenarioId = computed(() => String(route.params?.scenarioId ?? ''))

    const {
        runProjectsPageQueryV1,
        modelProjectsPageQueryV1
    } = useProjectsPageQueryV1(workspaceId, tchProjectId, scenarioId)

    const rm = modelProjectsPageQueryV1 as Ref<ProjectsPageReadModel | null>

    const portfolioId = computed(() => rm.value?.PortfolioId ?? null)

    // Plan picker query
    const {
        runScenarioProjectPlanPickerQueryV1,
        modelScenarioProjectPlanPickerQueryV1
    } = useScenarioProjectPlanPickerQueryV1(workspaceId, tchProjectId, scenarioId)

    const planPickerRm = modelScenarioProjectPlanPickerQueryV1 as Ref<ScenarioProjectPlanPickerReadModel | null>

    // ======================
    // State (kept 1:1)
    // ======================

    const searchPhrase = ref<string | null>(null)
    const acceptanceFilter = ref<ProjectAcceptance | null>(null)
    const kanbanFilter = ref<string | null>(null)
    const batchOperationEntities = ref<any[]>([])

    const addProjectsDialog = shallowReactive(new AddProjectsDialogViewModel())
    const startDateMonthPicker = shallowReactive(new MonthPickerDialogViewModel())
    const minStartDateMonthPicker = shallowReactive(new MonthPickerDialogViewModel())
    const maxStartDateMonthPicker = shallowReactive(new MonthPickerDialogViewModel())
    const commitmentDateMonthPicker = shallowReactive(new MonthPickerDialogViewModel())
    const projectPlanPickerDialog = shallowReactive(new ProjectPlanPickerDialogViewModel())

    // ======================
    // Derived (former getters)
    // ======================

    const noneSelected = computed(() => !rm.value!.Projects.some(a => a.UiSelected))
    const allSelected = computed(() => !rm.value!.Projects.some(a => !a.UiSelected))
    const anySelected = computed(() => rm.value!.Projects.some(a => a.UiSelected))

    const selected = computed<ScenarioProjectReadModel[]>(
        () => rm.value!.Projects.filter(a => a.UiSelected) || []
    )

    const projectsView = computed<ScenarioProjectReadModel[]>(() =>
    {
        let view = new Array(...rm.value!.Projects)

        if (searchPhrase.value)
        {
            const phrase = searchPhrase.value.toLowerCase()
            view = view.filter(a => a.Name.toLowerCase().indexOf(phrase) > -1)
        }

        if (acceptanceFilter.value)
        {
            return view.filter(a => a.Acceptance === acceptanceFilter.value)
        }

        if (kanbanFilter.value)
        {
            return view.filter(a => a.KanbanStatus === kanbanFilter.value)
        }

        return view
    })

    const filterLabel = computed(() =>
    {
        if (kanbanFilter.value) return kanbanFilter.value
        if (acceptanceFilter.value) return acceptanceFilter.value
        return 'All'
    })

    // ======================
    // UI actions (kept names / behaviour)
    // ======================

    function selectAll()
    {
        projectsView.value.forEach(a => (a.UiSelected = true))
    }

    function unselectAll()
    {
        rm.value!.Projects.forEach(a => (a.UiSelected = false))
    }

    function setFilter(newAcceptanceFilter: ProjectAcceptance | null, newKanbanFilter: string | null)
    {
        kanbanFilter.value = newKanbanFilter
        acceptanceFilter.value = newAcceptanceFilter
    }

    function showAddProjectsDialog()
    {
        addProjectsDialog.show(rm.value!.Lists, rm.value!.Statuses)
    }

    function showPlanPicker(p: ScenarioProjectReadModel)
    {
        if (p.Plans.length)
        {
            projectPlanPickerDialog.show(p.Id, p.Name, p.ActivePlanId, p.LockedPlanId)
            projectPlanPickerDialog.setData(p.Plans)
        } else
        {
            runScenarioProjectPlanPickerQueryV1({ ProjectId: p.Id })
            projectPlanPickerDialog.show(p.Id, p.Name, p.ActivePlanId, p.LockedPlanId)
        }
    }

    function showStartDateMonthPicker(project: ScenarioProjectReadModel | null)
    {
        batchOperationEntities.value = project != null ? [project.Id] : selected.value.map(a => a.Id)

        const startDate = project?.StartDate ?? project?.DefaultStartDate ?? null
        startDateMonthPicker.show(MonthYearHelpers.buildFromMonthYearString(startDate), 'Edit start date')
    }

    function showMinStartDateMonthPicker(project: ScenarioProjectReadModel | null)
    {
        batchOperationEntities.value = project != null ? [project.Id] : selected.value.map(a => a.Id)
        minStartDateMonthPicker.show(
            MonthYearHelpers.buildFromMonthYearString(project?.MinStartDate ?? null),
            'Edit min start date'
        )
    }

    function showMaxStartDateMonthPicker(project: ScenarioProjectReadModel | null)
    {
        batchOperationEntities.value = project != null ? [project.Id] : selected.value.map(a => a.Id)
        maxStartDateMonthPicker.show(
            MonthYearHelpers.buildFromMonthYearString(project?.MaxStartDate ?? null),
            'Edit max start date'
        )
    }

    function showCommitmentDateMonthPicker(project: ScenarioProjectReadModel | null)
    {
        batchOperationEntities.value = project != null ? [project.Id] : selected.value.map(a => a.Id)
        commitmentDateMonthPicker.show(
            MonthYearHelpers.buildFromMonthYearString(project?.CommitmentDate ?? null),
            'Edit commitment date'
        )
    }

    // ======================
    // Commands (composables)
    // ======================

    const {
        invokeAddProjectsToScenarioCommandV1,
        onSucceedAddProjectsToScenarioCommandV1
    } = useAddProjectsToScenarioCommandV1(workspaceId, portfolioId, scenarioId)

    const {
        invokeRemoveProjectsFromScenarioCommandV1,
        onSucceedRemoveProjectsFromScenarioCommandV1,
        command: removeProjectsFromScenarioCommandV1
    } = useRemoveProjectsFromScenarioCommandV1(workspaceId, portfolioId, scenarioId)

    const {
        invokeUpdateProjectScenarioAcceptanceCommandV1,
        onSucceedUpdateProjectScenarioAcceptanceCommandV1,
        result: resultUpdateProjectScenarioAcceptanceCommandV1,
        command: updateProjectScenarioAcceptanceCommandV1
    } = useUpdateProjectScenarioAcceptanceCommandV1(workspaceId, portfolioId, scenarioId)

    const {
        invokeLockProjectScenarioAcceptanceCommandV1,
        onSucceedLockProjectScenarioAcceptanceCommandV1,
        result: resultLockProjectScenarioAcceptanceCommandV1,
        command: lockProjectScenarioAcceptanceCommandV1
    } = useLockProjectScenarioAcceptanceCommandV1(workspaceId, portfolioId, scenarioId)

    const {
        invokeUnlockProjectScenarioAcceptanceCommandV1,
        onSucceedUnlockProjectScenarioAcceptanceCommandV1,
        result: resultUnlockProjectScenarioAcceptanceCommandV1,
        command: unlockProjectScenarioAcceptanceCommandV1
    } = useUnlockProjectScenarioAcceptanceCommandV1(workspaceId, portfolioId, scenarioId)

    const {
        invokeUpdateProjectScenarioCommitmentDateCommandV1,
        onSucceedUpdateProjectScenarioCommitmentDateCommandV1,
        result: resultUpdateProjectScenarioCommitmentDateCommandV1,
        command: updateProjectScenarioCommitmentDateCommandV1
    } = useUpdateProjectScenarioCommitmentDateCommandV1(workspaceId, portfolioId, scenarioId)

    const {
        invokeLockProjectScenarioCommitmentDateCommandV1,
        onSucceedLockProjectScenarioCommitmentDateCommandV1,
        result: resultLockProjectScenarioCommitmentDateCommandV1,
        command: lockProjectScenarioCommitmentDateCommandV1
    } = useLockProjectScenarioCommitmentDateCommandV1(workspaceId, portfolioId, scenarioId)

    const {
        invokeUnlockProjectScenarioCommitmentDateCommandV1,
        onSucceedUnlockProjectScenarioCommitmentDateCommandV1,
        result: resultUnlockProjectScenarioCommitmentDateCommandV1,
        command: unlockProjectScenarioCommitmentDateCommandV1
    } = useUnlockProjectScenarioCommitmentDateCommandV1(workspaceId, portfolioId, scenarioId)

    const {
        invokeUpdateProjectScenarioStartDateCommandV1,
        onSucceedUpdateProjectScenarioStartDateCommandV1,
        result: resultUpdateProjectScenarioStartDateCommandV1,
        command: updateProjectScenarioStartDateCommandV1
    } = useUpdateProjectScenarioStartDateCommandV1(workspaceId, portfolioId, scenarioId)

    const {
        invokeLockProjectScenarioStartDateCommandV1,
        onSucceedLockProjectScenarioStartDateCommandV1,
        result: resultLockProjectScenarioStartDateCommandV1,
        command: lockProjectScenarioStartDateCommandV1
    } = useLockProjectScenarioStartDateCommandV1(workspaceId, portfolioId, scenarioId)

    const {
        invokeUnlockProjectScenarioStartDateCommandV1,
        onSucceedUnlockProjectScenarioStartDateCommandV1,
        result: resultUnlockProjectScenarioStartDateCommandV1,
        command: unlockProjectScenarioStartDateCommandV1
    } = useUnlockProjectScenarioStartDateCommandV1(workspaceId, portfolioId, scenarioId)

    const {
        invokeUpdateProjectScenarioMinStartDateCommandV1,
        onSucceedUpdateProjectScenarioMinStartDateCommandV1,
        result: resultUpdateProjectScenarioMinStartDateCommandV1,
        command: updateProjectScenarioMinStartDateCommandV1
    } = useUpdateProjectScenarioMinStartDateCommandV1(workspaceId, portfolioId, scenarioId)

    const {
        invokeUpdateProjectScenarioMaxStartDateCommandV1,
        onSucceedUpdateProjectScenarioMaxStartDateCommandV1,
        result: resultUpdateProjectScenarioMaxStartDateCommandV1,
        command: updateProjectScenarioMaxStartDateCommandV1
    } = useUpdateProjectScenarioMaxStartDateCommandV1(workspaceId, portfolioId, scenarioId)

    const {
        invokeUpdateProjectScenarioPlanLockCommandV1,
        onSucceedUpdateProjectScenarioPlanLockCommandV1,
        result: resultUpdateProjectScenarioPlanLockCommandV1,
        command: updateProjectScenarioPlanLockCommandV1
    } = useUpdateProjectScenarioPlanLockCommandV1(workspaceId, portfolioId, scenarioId)

    // ======================
    // Template handlers (kept names / signatures)
    // ======================

    function handleUpdateProjectScenarioPlanLockCommand(e: { projectId: string; lockedPlanId: string | null })
    {
        invokeUpdateProjectScenarioPlanLockCommandV1({
            ProjectId: e.projectId,
            LockedPlanId: e.lockedPlanId
        })
    }

    function handleLockProjectScenarioAcceptanceCommand(projectId: string | null)
    {
        const ids = projectId != null ? [projectId] : selected.value.map(a => a.Id)
        invokeLockProjectScenarioAcceptanceCommandV1({ ProjectIds: ids })
    }

    function handleUnlockProjectScenarioAcceptanceCommand(projectId: string | null)
    {
        const ids = projectId != null ? [projectId] : selected.value.map(a => a.Id)
        invokeUnlockProjectScenarioAcceptanceCommandV1({ ProjectIds: ids })
    }

    function handleLockProjectScenarioStartDateCommand(projectId: string | null)
    {
        const ids = projectId != null ? [projectId] : selected.value.map(a => a.Id)
        invokeLockProjectScenarioStartDateCommandV1({ ProjectIds: ids })
    }

    function handleUnlockProjectScenarioStartDateCommand(projectId: string | null)
    {
        const ids = projectId != null ? [projectId] : selected.value.map(a => a.Id)
        invokeUnlockProjectScenarioStartDateCommandV1({ ProjectIds: ids })
    }

    function handleLockProjectScenarioCommitmentDateCommand(projectId: string | null)
    {
        const ids = projectId != null ? [projectId] : selected.value.map(a => a.Id)
        invokeLockProjectScenarioCommitmentDateCommandV1({ ProjectIds: ids })
    }

    function handleUnlockProjectScenarioCommitmentDateCommand(projectId: string | null)
    {
        const ids = projectId != null ? [projectId] : selected.value.map(a => a.Id)
        invokeUnlockProjectScenarioCommitmentDateCommandV1({ ProjectIds: ids })
    }

    function handleUpdateProjectScenarioStartDateCommand(date: MonthYear | null)
    {
        invokeUpdateProjectScenarioStartDateCommandV1({
            ProjectIds: batchOperationEntities.value,
            StartDate: date
        })
    }

    function handleUpdateProjectScenarioMinStartDateCommand(date: MonthYear | null)
    {
        invokeUpdateProjectScenarioMinStartDateCommandV1({
            ProjectIds: batchOperationEntities.value,
            MinStartDate: date
        })
    }

    function handleUpdateProjectScenarioMaxStartDateCommand(date: MonthYear | null)
    {
        invokeUpdateProjectScenarioMaxStartDateCommandV1({
            ProjectIds: batchOperationEntities.value,
            MaxStartDate: date
        })
    }

    function handleUpdateProjectScenarioCommitmentDateCommand(date: MonthYear | null)
    {
        invokeUpdateProjectScenarioCommitmentDateCommandV1({
            ProjectIds: batchOperationEntities.value,
            CommitmentDate: date
        })
    }

    function handleAddProjectsToScenarioCommand(viewDefId: string | null, statusId: number | null)
    {
        invokeAddProjectsToScenarioCommandV1({
            ViewDefId: viewDefId,
            KanbanStatusId: statusId
        })
    }

    function handleRemoveProjectsFromScenarioCommand(ids: string[] | null)
    {
        if (confirm('Are you sure you want to remove the selected items from this scenario?'))
        {
            const resolvedIds = ids ?? selected.value.map(a => a.Id)
            invokeRemoveProjectsFromScenarioCommandV1({ ProjectIds: resolvedIds })
        }
    }

    function handleUpdateProjectScenarioAcceptanceCommand(acceptance: string, id: string | null)
    {
        const ids = id != null ? [id] : selected.value.map(a => a.Id)
        invokeUpdateProjectScenarioAcceptanceCommandV1({
            ProjectIds: ids,
            Acceptance: acceptance
        })
    }

    function onSelected(_result: any)
    {
        runProjectsPageQueryV1()
    }

    function onScenarioUpdated()
    {
        runProjectsPageQueryV1()
    }


    onMounted(() =>
    {
        document.title = 'Portfolio Plan - Projects'
        runProjectsPageQueryV1()
    })

    watch(planPickerRm, (current, previous) =>
    {
        if (current && current !== previous)
        {
            const p = rm.value!.Projects.find(a => a.Id === current.ProjectId)
            if (p)
            {
                p.Plans = current.Plans
                projectPlanPickerDialog.setData(current.Plans)
            }
        }
    })

    onSucceedAddProjectsToScenarioCommandV1(() =>
    {
        runProjectsPageQueryV1()
        addProjectsDialog.close()
    })

    onSucceedRemoveProjectsFromScenarioCommandV1(() =>
    {
        const cmd = removeProjectsFromScenarioCommandV1.value as RemoveProjectsFromScenarioCommandV1

        rm.value!.Projects = rm.value!.Projects.filter(p => !cmd.ProjectIds.includes(p.Id))
    })

    onSucceedUpdateProjectScenarioAcceptanceCommandV1(() =>
    {
        const cmd = updateProjectScenarioAcceptanceCommandV1.value as UpdateProjectScenarioAcceptanceCommandV1
        const acceptance = cmd.Acceptance as ProjectAcceptance
        const ids = (resultUpdateProjectScenarioAcceptanceCommandV1.value as string).split(';')

       rm.value.Projects.filter(p => ids.includes(p.Id)).forEach(p => p.Acceptance = acceptance)
    })

    onSucceedLockProjectScenarioAcceptanceCommandV1(() =>
    {
        const cmd = lockProjectScenarioAcceptanceCommandV1.value as LockProjectScenarioAcceptanceCommandV1
        const ids = (resultLockProjectScenarioAcceptanceCommandV1.value as string).split(';')

        rm.value.Projects.filter(p => ids.includes(p.Id)).forEach(p => (p.AcceptanceLocked = true))
    })

    onSucceedUnlockProjectScenarioAcceptanceCommandV1(() =>
    {
        const cmd = unlockProjectScenarioAcceptanceCommandV1.value as UnlockProjectScenarioAcceptanceCommandV1
        const ids = (resultUnlockProjectScenarioAcceptanceCommandV1.value as string).split(';')

        rm.value!.Projects.filter(p => ids.includes(p.Id)).forEach(p => (p.AcceptanceLocked = false))
    })

    onSucceedUpdateProjectScenarioCommitmentDateCommandV1(() =>
    {
        const cmd = updateProjectScenarioCommitmentDateCommandV1.value as UpdateProjectScenarioCommitmentDateCommandV1
        const commitmentDate = MonthYearHelpers.toMonthYearString(cmd.CommitmentDate ?? null)
        const ids = (resultUpdateProjectScenarioCommitmentDateCommandV1.value as string).split(';')

        rm.value!.Projects.filter(p => ids.includes(p.Id)).forEach(p => (p.CommitmentDate = commitmentDate))
    })

    onSucceedLockProjectScenarioCommitmentDateCommandV1(() =>
    {
        const ids = (resultLockProjectScenarioCommitmentDateCommandV1.value as string).split(';')

        rm.value.Projects.filter(p => ids.includes(p.Id)).forEach(p => (p.CommitmentDateLocked = true))
    })

    onSucceedUnlockProjectScenarioCommitmentDateCommandV1(() =>
    {
        const ids = (resultUnlockProjectScenarioCommitmentDateCommandV1.value as string).split(';')

        rm.value.Projects.filter(p => ids.includes(p.Id)).forEach(p => (p.CommitmentDateLocked = false))
    })

    onSucceedUpdateProjectScenarioStartDateCommandV1(() =>
    {
        const cmd = updateProjectScenarioStartDateCommandV1.value as UpdateProjectScenarioStartDateCommandV1
        const startDate = MonthYearHelpers.toMonthYearString(cmd.StartDate ?? null)
        const ids = (resultUpdateProjectScenarioStartDateCommandV1.value as string).split(';')

        rm.value.Projects.filter(p => ids.includes(p.Id)).forEach(p => (p.StartDate = startDate))
    })

    onSucceedLockProjectScenarioStartDateCommandV1(() =>
    {
        const ids = (resultLockProjectScenarioStartDateCommandV1.value as string).split(';')

        rm.value.Projects.filter(p => ids.includes(p.Id)).forEach(p => (p.StartDateLocked = true))
    })

    onSucceedUnlockProjectScenarioStartDateCommandV1(() =>
    {
        const ids = (resultUnlockProjectScenarioStartDateCommandV1.value as string).split(';')

        rm.value.Projects.filter(p => ids.includes(p.Id)).forEach(p => (p.StartDateLocked = false))
    })

    onSucceedUpdateProjectScenarioMinStartDateCommandV1(() =>
    {
        const cmd = updateProjectScenarioMinStartDateCommandV1.value as UpdateProjectScenarioMinStartDateCommandV1
        const minStartDate = MonthYearHelpers.toMonthYearString(cmd.MinStartDate ?? null)
        const ids = (resultUpdateProjectScenarioMinStartDateCommandV1.value as string).split(';')

        rm.value.Projects.filter(p => ids.includes(p.Id)).forEach(p => (p.MinStartDate = minStartDate))
    })

    onSucceedUpdateProjectScenarioPlanLockCommandV1(() =>
    {
        const cmd = updateProjectScenarioPlanLockCommandV1.value as UpdateProjectScenarioPlanLockCommandV1

        const project = rm.value.Projects.find(a => a.Id === cmd.ProjectId)

        if (project)
        {
            project.LockedPlanId = cmd.LockedPlanId
            project.PlanId = project.LockedPlanId ?? project.ActivePlanId

            const plan = project.Plans?.find(p => p.Id === project.PlanId)
            project.PlanName = plan?.Name ?? null
        }

        projectPlanPickerDialog.close()
    })

    onSucceedUpdateProjectScenarioMaxStartDateCommandV1(() =>
    {
        const cmd = updateProjectScenarioMaxStartDateCommandV1.value as UpdateProjectScenarioMaxStartDateCommandV1
        const maxStartDate = MonthYearHelpers.toMonthYearString(cmd.MaxStartDate ?? null)
        const ids = (resultUpdateProjectScenarioMaxStartDateCommandV1.value as string).split(';')

        rm.value.Projects.filter(p => ids.includes(p.Id)).forEach(p => (p.MaxStartDate = maxStartDate))
    })
</script>

<template>
    <div>
        <MonthPickerDialog :viewModel="startDateMonthPicker" @update-date="handleUpdateProjectScenarioStartDateCommand" />
        <MonthPickerDialog :viewModel="minStartDateMonthPicker" @update-date="handleUpdateProjectScenarioMinStartDateCommand" />
        <MonthPickerDialog :viewModel="maxStartDateMonthPicker" @update-date="handleUpdateProjectScenarioMaxStartDateCommand" />
        <MonthPickerDialog :viewModel="commitmentDateMonthPicker" @update-date="handleUpdateProjectScenarioCommitmentDateCommand" />

        <AddProjectsDialog :viewModel="addProjectsDialog" @selection-confirmed="handleAddProjectsToScenarioCommand" />
        <ProjectPlanPickerDialog :viewModel="projectPlanPickerDialog" @confirmed="handleUpdateProjectScenarioPlanLockCommand" />

        <PortfolioNavigation :workspaceId="workspaceId" :projectId="tchProjectId" />

        <div v-if="rm" class="container-fluid" style="margin-top: 80px; padding-top: 20px;">
            <div class="row">
                <div class="col-lg-6">
                    <small class="text-muted">{{rm.PortfolioName}} / {{rm.ScenarioName}}</small>
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Plan: Projects</h1>
                </div>
                <div class="col-lg-6 d-flex flex-column justify-content-end">
                    <PortfolioScenarioNavigation :portfolioId="rm.PortfolioId" 
                                                 :corporateScenarioUrl="rm.CorporateScenarioUrl"
                                                 @selected="onSelected"
                                                 @scenario-updated="onScenarioUpdated"/>
                </div>
            </div>

            <hr />

            <nav class="navbar navbar-light navbar-expand-md  mt-5" style="background-color: rgba(0, 0, 0, 0.03); border: 1px solid rgba(0, 0, 0, 0.125); border-bottom: 0;" role="navigation">

                <ul class="navbar-nav">

                    <li class="dropdown mr-2">
                        <div class="navbar-form">
                            <button type="button" class="btn btn-outline-secondary" @click="showAddProjectsDialog">
                                <i class="fa-solid fa-plus"></i> Add
                            </button>
                        </div>
                    </li>
                    <li class="dropdown mr-2">
                        <div class="navbar-form">
                            <button class="btn btn-outline-secondary" :disabled="selected.length === 0" @click="handleRemoveProjectsFromScenarioCommand(null)">
                                <i class="fa-regular fa-trash-can"></i>
                            </button>
                        </div>
                    </li>
                </ul>

                <div class="btn-group mr-2" role="group">
                    <div class="btn-group">
                        <button class="btn btn-outline-secondary" data-toggle="dropdown" :disabled="selected.length === 0">
                            Selection
                        </button>
                        <div class="dropdown-menu">
                            <a class="dropdown-item" @click="handleUpdateProjectScenarioAcceptanceCommand('Accepted', null)">Accepted</a>
                            <a class="dropdown-item" @click="handleUpdateProjectScenarioAcceptanceCommand('Rejected', null)">Rejected</a>
                            <a class="dropdown-item" @click="handleUpdateProjectScenarioAcceptanceCommand('TBD', null)">TBD</a>
                        </div>
                    </div>
                    <div class="btn-group" title="Lock / Unlock">
                        <button class="btn btn-outline-secondary custom-rounded-right" data-toggle="dropdown" :disabled="selected.length === 0">
                            <i class="fa-solid fa-lock"></i>
                        </button>
                        <div class="dropdown-menu">
                            <a class="dropdown-item" @click="handleLockProjectScenarioAcceptanceCommand(null)">Lock</a>
                            <a class="dropdown-item" @click="handleUnlockProjectScenarioAcceptanceCommand(null)">Unlock</a>
                        </div>
                    </div>
                </div>

                <div class="btn-group mr-2" role="group">
                    <button class="btn btn-outline-secondary" @click="showCommitmentDateMonthPicker(null)" :disabled="selected.length === 0">
                        Commitment
                    </button>
                    <div class="btn-group" title="Lock / Unlock">
                        <button class="btn btn-outline-secondary custom-rounded-right" data-toggle="dropdown" :disabled="selected.length === 0">
                            <i class="fa-solid fa-lock"></i>
                        </button>
                        <div class="dropdown-menu">
                            <a class="dropdown-item" @click="handleLockProjectScenarioCommitmentDateCommand(null)">Lock</a>
                            <a class="dropdown-item" @click="handleUnlockProjectScenarioCommitmentDateCommand(null)">Unlock</a>
                        </div>
                    </div>
                </div>

                <div class="btn-group mr-2" role="group">
                    <button class="btn btn-outline-secondary" @click="showStartDateMonthPicker(null)" :disabled="selected.length === 0">
                        Start date
                    </button>
                    <div class="btn-group" title="Lock / Unlock">
                        <button class="btn btn-outline-secondary custom-rounded-right" data-toggle="dropdown" :disabled="selected.length === 0">
                            <i class="fa-solid fa-lock"></i>
                        </button>
                        <div class="dropdown-menu">
                            <a class="dropdown-item" @click="handleLockProjectScenarioStartDateCommand(null)">Lock</a>
                            <a class="dropdown-item" @click="handleUnlockProjectScenarioStartDateCommand(null)">Unlock</a>
                        </div>
                    </div>
                </div>

                <div class="btn-group mr-2">
                    <button class="btn btn-outline-secondary custom-rounded-right" data-toggle="dropdown" :disabled="selected.length === 0">
                        Start range
                    </button>
                    <div class="dropdown-menu">
                        <a class="dropdown-item" @click="showMinStartDateMonthPicker(null)">Min start date</a>
                        <a class="dropdown-item" @click="showMaxStartDateMonthPicker(null)">Max start date</a>
                    </div>
                </div>



                <ul class="navbar-nav ml-auto mr-2">
                    <li class="dropdown">
                        <div class="navbar-form">
                            <div class="btn-group">
                                <button class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">Filter: {{filterLabel}}</button>
                                <div class="dropdown-menu">
                                    <a class="dropdown-item" @click="setFilter(null, null)">All</a>
                                    <div class="dropdown-divider"></div>
                                    <h6 class="dropdown-header">Filter by selection status:</h6>
                                    <a class="dropdown-item" @click="setFilter('TBD', null)">TBD</a>
                                    <a class="dropdown-item" @click="setFilter('Accepted', null)">Accepted</a>
                                    <a class="dropdown-item" @click="setFilter('Rejected', null)">Rejected</a>
                                    <div class="dropdown-divider"></div>
                                    <h6 class="dropdown-header">Filter by Kanban status:</h6>
                                    <a class="dropdown-item" v-for="s in rm.Statuses" :key="s.Id" @click="setFilter(null, s.Name)">{{s.Name}}</a>
                                </div>
                            </div>
                        </div>
                    </li>
                </ul>


                <form class="form-inline my-2 my-lg-0" role="search">
                    <input class="form-control mr-sm-2" type="search" placeholder="Search" v-model="searchPhrase">
                </form>
            </nav>


            <table class="table table-bordered table-hover">
                <thead>
                    <tr>
                        <th style="width: 40px; min-width: 40px !important;">
                            <div class="btn-group">
                                <a class="pointer" v-if="noneSelected" @click="selectAll"><i class="fa-regular fa-square"></i></a>
                                <a class="pointer" v-if="anySelected && !allSelected" @click="unselectAll"><i class="fa fa-minus-square"></i></a>
                                <a class="pointer" v-if="anySelected && allSelected" @click="unselectAll"><i class="fa-solid fa-square-check"></i></a>
                            </div>
                        </th>
                        <th class="text-left">Project</th>
                        <th>P&L</th>
                        <th>Status</th>
                        <th colspan="2">Selection</th>
                        <th colspan="2">Commitment</th>
                        <th colspan="2">Start date</th>
                        <th>Min start date</th>
                        <th>Max start date</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="a in projectsView" :key="a.Id">
                        <td><input type="checkbox" v-model="a.UiSelected" /></td>
                        <td>
                            <!-- Line 1: Project (primary link) -->
                            <div>
                                <a class="text-primary"
                                   v-sanitizedHref="`/#/portfolio/alternative/dashboard/${workspaceId}/${tchProjectId}/${a.Id}`">
                                    {{ a.Name }}
                                </a>
                            </div>

                            <!-- Line 2: Plan (click opens picker) -->
                            <div class="d-flex align-items-center mt-1 plan-meta">
                                <div class="plan-text">
                                    <!-- EFFECTIVE PLAN AVAILABLE -->
                                    <template v-if="a.PlanId">
                                        <!-- Locked to a plan -->
                                        <template v-if="a.LockedPlanId">
                                            <a class="small plan-toggle d-inline-flex align-items-center"
                                               :class="'text-locked-plan'"
                                               role="button"
                                               :title="a.PlanName ? `${a.PlanName} (locked)` : 'Locked plan'"
                                               @click.prevent="showPlanPicker(a)">
                                                {{ a.PlanName || 'Unnamed plan' }}
                                                <i class="fa-solid fa-caret-down ml-1 plan-caret" aria-hidden="true"></i>
                                            </a>
                                        </template>

                                        <!-- Following Active (no lock) -->
                                        <template v-else>
                                            <a class="small plan-toggle d-inline-flex align-items-center"
                                               :class="a.ActivePlanId ? 'text-muted' : 'text-danger'"
                                               role="button"
                                               :title="a.ActivePlanId ? `${a.PlanName || 'Unnamed plan'} (follows Active)` : 'No Active plan set — click to choose'"
                                               @click.prevent="showPlanPicker(a)">
                                                {{ a.PlanName || 'Unnamed plan' }}
                                                <i class="fa-solid fa-caret-down ml-1 plan-caret" aria-hidden="true"></i>
                                            </a>
                                        </template>
                                    </template>

                                    <!-- NO EFFECTIVE PLAN -->
                                    <template v-else>
                                        <a class="small text-danger plan-toggle d-inline-flex align-items-center"
                                           role="button"
                                           title="No plan available — click to choose"
                                           @click.prevent="showPlanPicker(a)">
                                            No plan
                                            <i class="fa-solid fa-caret-down ml-1 plan-caret" aria-hidden="true"></i>
                                        </a>
                                    </template>
                                </div>
                            </div>
                        </td>

                        <!-- P&L -->
                        <td class="text-center align-middle">
                            <a v-if="a.PlanId"
                               class="text-primary"
                               :href="`/#/q/project/pnl/${workspaceId}/${tchProjectId}/${a.Id}/${a.PlanId}`">
                                View
                            </a>
                            <a v-else
                               class="text-danger"
                               :href="`/#/q/project/summary/${workspaceId}/${tchProjectId}/${a.Id}`">
                                NA
                            </a>
                        </td>

                        <!-- Status -->
                        <td class="text-center align-middle">
                            {{ a.KanbanStatus }}
                        </td>



                        <td class="text-center align-middle">
                            <template v-if="a.AcceptanceLocked === true">
                                {{a.Acceptance}}
                            </template>
                            <template v-if="a.AcceptanceLocked === false">
                                <div class="btn-group">
                                    <a class="text-primary dropdown-toggle" data-toggle="dropdown">
                                        {{a.Acceptance}}
                                    </a>
                                    <div class="dropdown-menu">
                                        <a class="dropdown-item" @click="handleUpdateProjectScenarioAcceptanceCommand('Accepted', a.Id)">Accepted</a>
                                        <a class="dropdown-item" @click="handleUpdateProjectScenarioAcceptanceCommand('Rejected', a.Id)">Rejected</a>
                                        <a class="dropdown-item" @click="handleUpdateProjectScenarioAcceptanceCommand('TBD', a.Id)">TBD</a>
                                    </div>
                                </div>
                            </template>
                        </td>
                        <td class="text-center align-middle" style="width: 40px;">
                            <button title="Unlock acceptance" v-if="a.AcceptanceLocked === true" class="btn btn-outline-secondary btn-sm" @click="handleUnlockProjectScenarioAcceptanceCommand(a.Id)">
                                <i class="fa-solid fa-lock"></i>
                            </button>
                            <button title="Lock acceptance" v-if="a.AcceptanceLocked === false" class="btn btn-outline-secondary btn-sm" @click="handleLockProjectScenarioAcceptanceCommand(a.Id)" :disabled="a.Acceptance === 'TBD'">
                                <i class="fa-solid fa-lock-open"></i>
                            </button>
                        </td>
                        <td class="text-center align-middle">
                            <template v-if="a.CommitmentDateLocked === true">
                                {{a.CommitmentDate ?? 'Not set'}}
                            </template>
                            <template v-if="a.CommitmentDateLocked === false">
                                <a class="text-primary" @click="showCommitmentDateMonthPicker(a)">
                                    {{a.CommitmentDate ?? 'Not set'}}
                                </a>
                            </template>
                        </td>
                        <td class="text-center align-middle" style="width: 40px;">
                            <button title="Unlock commitment date" v-if="a.CommitmentDateLocked === true" class="btn btn-outline-secondary btn-sm" @click="handleUnlockProjectScenarioCommitmentDateCommand(a.Id)">
                                <i class="fa-solid fa-lock"></i>
                            </button>
                            <button title="Lock commitment date" v-if="a.CommitmentDateLocked === false" class="btn btn-outline-secondary btn-sm" @click="handleLockProjectScenarioCommitmentDateCommand(a.Id)">
                                <i class="fa-solid fa-lock-open"></i>
                            </button>
                        </td>
                        <td class="text-center align-middle">
                            <template v-if="a.StartDateLocked === true">
                                {{a.StartDate}}
                            </template>
                            <template v-if="a.StartDateLocked === false">
                                <a v-if="a.StartDate" class="text-primary" @click="showStartDateMonthPicker(a)">
                                    {{a.StartDate}}
                                </a>
                                <a v-if="!a.StartDate" class="text-muted pointer" @click="showStartDateMonthPicker(a)" title="Not set - default value">
                                    Not set
                                </a>
                            </template>
                        </td>
                        <td class="text-center align-middle" style="width: 40px;">
                            <button title="Unlock start date" v-if="a.StartDateLocked === true" class="btn btn-outline-secondary btn-sm" @click="handleUnlockProjectScenarioStartDateCommand(a.Id)">
                                <i class="fa-solid fa-lock"></i>
                            </button>
                            <button title="Lock start date" :disabled="!a.StartDate" v-if="a.StartDateLocked === false" class="btn btn-outline-secondary btn-sm" @click="handleLockProjectScenarioStartDateCommand(a.Id)">
                                <i class="fa-solid fa-lock-open"></i>
                            </button>
                        </td>
                        <td class="text-center align-middle">
                            <a class="text-primary" @click="showMinStartDateMonthPicker(a)">
                                {{a.MinStartDate ?? 'Not set'}}
                            </a>
                        </td>
                        <td class="text-center align-middle">
                            <a class="text-primary" @click="showMaxStartDateMonthPicker(a)">
                                {{a.MaxStartDate ?? 'Not set'}}
                            </a>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <br />
        <br />
        <br />
        <br />

    </div>

</template>

<style scoped>
    th { 
        text-align: center; 
    }

    .custom-rounded-right {
        border-top-right-radius: 0.25rem !important; /* Adjust based on your theme */
        border-bottom-right-radius: 0.25rem !important;
    }

    .plan-meta {
        font-size: 0.85rem;
        color: #6c757d;
        min-width: 0;
    }

    .plan-text {
        min-width: 0;
    }

    .plan-toggle {
        cursor: pointer;
        max-width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .plan-view {
        white-space: nowrap;
    }

    .plan-caret {
        color: #6c757d; /* muted */
        transition: color 0.15s ease-in-out;
    }

    /* bump specificity a bit for scoped styles */
    a.plan-toggle:hover .plan-caret,
    a.plan-toggle:focus .plan-caret {
        color: #343a40; /* darker on hover/focus */
    }

    .text-locked-plan {
        color: #e0a800; /* slightly deeper than #ffc107 */
        font-weight: 500;
    }


</style>
